import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Phone as PhoneIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import TranslationsEditor from '../../components/TranslationsEditor';
import BulkTranslateButton from '../../components/BulkTranslateButton';
import DefaultsControls from '../../components/DefaultsControls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyPhone = { label: '', number: '', hours: '' };

const ContactManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contact`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load Contact content'))
      .finally(() => setLoading(false));
  }, []);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const updatePhone = (idx, patch) => {
    const next = [...(data.phones || [])];
    next[idx] = { ...next[idx], ...patch };
    update({ phones: next });
  };
  const addPhone = () => update({ phones: [...(data.phones || []), { ...emptyPhone }] });
  const removePhone = (idx) => update({ phones: (data.phones || []).filter((_, i) => i !== idx) });

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...data,
        phones: (data.phones || []).filter((p) => (p.number || '').trim()),
      };
      await axios.put(`${API}/contact`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Contact page saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) return <p className="text-maroon/60">Loading…</p>;

  return (
    <div data-testid="contact-manager" className="max-w-4xl">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-heading font-bold text-maroon flex items-center gap-2">
            <PhoneIcon size={28} className="text-terracotta" />
            Contact Page
          </h2>
          <p className="text-maroon/60 text-sm mt-1">Edit the content shown at /contact. Changes apply immediately on save.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DefaultsControls
            section="contact"
            reseedConfirm={
              'Restore the Contact page from your last saved snapshot?\n\n' +
              'This will overwrite the hero, phones, email, address, map embed, CTA, and translations. ' +
              'If no snapshot has been saved yet, falls back to the original factory copy.'
            }
            onComplete={() => {
              axios.get(`${API}/contact`)
                .then((res) => setData(res.data))
                .catch(() => {});
            }}
            reseedTestId="reseed-contact-btn"
            snapshotTestId="snapshot-contact-btn"
          />
          <Button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-contact-btn">
            <Save size={16} />
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="card mb-6">
        <h3 className="font-heading font-bold text-xl text-maroon mb-4">Hero</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Headline</Label>
            <Input
              value={data.hero_title}
              onChange={(e) => update({ hero_title: e.target.value })}
              className="input-field"
              data-testid="contact-hero-title"
            />
          </div>
          <div className="space-y-2">
            <Label>Subheading</Label>
            <Textarea
              value={data.hero_subtitle}
              onChange={(e) => update({ hero_subtitle: e.target.value })}
              rows={3}
              className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
              data-testid="contact-hero-subtitle"
            />
          </div>
        </div>
      </section>

      {/* Phones */}
      <section className="card mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-heading font-bold text-xl text-maroon">Phone Numbers</h3>
          <Button type="button" variant="outline" size="sm" onClick={addPhone} className="border-maroon/20" data-testid="add-contact-phone-btn">
            <Plus size={14} className="mr-1" /> Add Phone
          </Button>
        </div>

        {(data.phones || []).length === 0 && (
          <p className="text-sm text-maroon/50 italic">No phone numbers yet — add one above.</p>
        )}

        <div className="space-y-3">
          {(data.phones || []).map((p, idx) => (
            <div key={idx} className="border border-maroon/15 rounded-sm p-4 bg-sand/20" data-testid={`contact-phone-${idx}`}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Label</Label>
                  <Input
                    value={p.label}
                    onChange={(e) => updatePhone(idx, { label: e.target.value })}
                    placeholder="e.g., Call Us, WhatsApp"
                    className="input-field"
                    data-testid={`contact-phone-label-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Number</Label>
                  <Input
                    value={p.number}
                    onChange={(e) => updatePhone(idx, { number: e.target.value })}
                    placeholder="e.g., (833) 568-8090"
                    className="input-field"
                    data-testid={`contact-phone-number-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Hours / Note</Label>
                  <Input
                    value={p.hours}
                    onChange={(e) => updatePhone(idx, { hours: e.target.value })}
                    placeholder="e.g., Mon-Fri 9am-6pm EST"
                    className="input-field"
                    data-testid={`contact-phone-hours-${idx}`}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => removePhone(idx)}
                className="mt-3 text-coral hover:bg-coral/10 px-3 py-1 rounded-sm text-sm font-semibold inline-flex items-center gap-1"
                data-testid={`remove-contact-phone-${idx}`}
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Email */}
      <section className="card mb-6">
        <h3 className="font-heading font-bold text-xl text-maroon mb-4">Email</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Email Address</Label>
            <Input
              value={data.email}
              onChange={(e) => update({ email: e.target.value })}
              type="email"
              className="input-field"
              data-testid="contact-email"
            />
          </div>
          <div>
            <Label>Response Time Note</Label>
            <Input
              value={data.email_response_time}
              onChange={(e) => update({ email_response_time: e.target.value })}
              placeholder="e.g., We respond within 24 hours"
              className="input-field"
              data-testid="contact-email-response-time"
            />
          </div>
        </div>
      </section>

      {/* Address & Map */}
      <section className="card mb-6">
        <h3 className="font-heading font-bold text-xl text-maroon mb-4">Address & Map</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Section Label</Label>
              <Input
                value={data.address_label}
                onChange={(e) => update({ address_label: e.target.value })}
                placeholder="e.g., Visit Us"
                className="input-field"
                data-testid="contact-address-label"
              />
            </div>
            <div>
              <Label>Address Note (when no physical address)</Label>
              <Input
                value={data.address_note}
                onChange={(e) => update({ address_note: e.target.value })}
                placeholder="e.g., Currently operating remotely"
                className="input-field"
                data-testid="contact-address-note"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Physical Address (optional)</Label>
            <Textarea
              value={data.address}
              onChange={(e) => update({ address: e.target.value })}
              rows={2}
              placeholder="123 Anywhere St, Miami FL 33101"
              className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
              data-testid="contact-address"
            />
          </div>

          <div className="space-y-2">
            <Label>Google Maps Embed URL</Label>
            <Textarea
              value={data.map_embed_url}
              onChange={(e) => update({ map_embed_url: e.target.value })}
              rows={3}
              placeholder='Paste the "src" URL from Google Maps → Share → Embed a map → HTML &lt;iframe src="...">'
              className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none font-mono text-xs"
              data-testid="contact-map-embed-url"
            />
            <p className="text-xs text-maroon/50">
              Open Google Maps → click <strong>Share</strong> → <strong>Embed a map</strong> → copy <em>only</em> the <code>src=</code> URL (starts with <code>https://www.google.com/maps/embed?pb=...</code>).
            </p>
            {data.map_embed_url && (
              <div className="mt-3 border border-maroon/15 rounded-sm overflow-hidden">
                <iframe
                  src={data.map_embed_url}
                  title="Map preview"
                  className="w-full h-64"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map-preview"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="card mb-6">
        <h3 className="font-heading font-bold text-xl text-maroon mb-4">Bottom CTA Banner</h3>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input
              value={data.cta_title}
              onChange={(e) => update({ cta_title: e.target.value })}
              className="input-field"
              data-testid="contact-cta-title"
            />
          </div>
          <div>
            <Label>Text</Label>
            <Textarea
              value={data.cta_text}
              onChange={(e) => update({ cta_text: e.target.value })}
              rows={2}
              className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
              data-testid="contact-cta-text"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Button Text</Label>
              <Input
                value={data.cta_button_text}
                onChange={(e) => update({ cta_button_text: e.target.value })}
                className="input-field"
                data-testid="contact-cta-button-text"
              />
            </div>
            <div>
              <Label>Button Link</Label>
              <Input
                value={data.cta_button_link}
                onChange={(e) => update({ cta_button_link: e.target.value })}
                placeholder="/consultation"
                className="input-field"
                data-testid="contact-cta-button-link"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Translations */}
      <section className="card mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-heading font-bold text-xl text-maroon">Translations</h3>
          <BulkTranslateButton
            scope="contact"
            onComplete={() => {
              const token = localStorage.getItem('token');
              axios.get(`${API}/contact`, { headers: { Authorization: `Bearer ${token}` } })
                .then((res) => setData(res.data))
                .catch(() => {});
            }}
          />
        </div>
        <TranslationsEditor
          translations={data.translations || {}}
          onChange={(translations) => update({ translations })}
          sourceContent={{
            hero_title: data.hero_title,
            hero_subtitle: data.hero_subtitle,
            email_response_time: data.email_response_time,
            address_label: data.address_label,
            address: data.address,
            address_note: data.address_note,
            cta_title: data.cta_title,
            cta_text: data.cta_text,
            cta_button_text: data.cta_button_text,
          }}
          fields={[
            { key: 'hero_title', label: 'Hero Title' },
            { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
            { key: 'email_response_time', label: 'Email Response Time' },
            { key: 'address_label', label: 'Address Section Label' },
            { key: 'address_note', label: 'Address Note' },
            { key: 'cta_title', label: 'CTA Title' },
            { key: 'cta_text', label: 'CTA Text', type: 'textarea' },
            { key: 'cta_button_text', label: 'CTA Button Text' },
          ]}
          languages={['es']}
        />
        <p className="text-xs text-maroon/50 mt-3">
          Tip: click <strong>Translate to Spanish</strong> to auto-fill all Spanish fields. Phone numbers and email addresses are not translated.
        </p>
      </section>

      <div className="flex justify-end mb-12">
        <Button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2">
          <Save size={16} />
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default ContactManager;
